<template>
  <div>
    <pa-admin-toolbar
      :per-page="perPage"
      :search.sync="searchInput"
      :from.sync="fromInput"
      :to.sync="toInput"
      :busy="loading"
      @per-page="setPerPage"
      @search="applySearch"
    >
      <button type="button" class="paa-btn paa-btn--secondary" :disabled="loading || !total" @click="exportRows">
        <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" aria-hidden="true"><path d="M12 3v12m0 0 4-4m-4 4-4-4M4 21h16" /></svg>
        Export All
      </button>
    </pa-admin-toolbar>

    <div class="paa-filters">
      <pa-admin-filter label="SEO Manager" :value="filters.campaignManager" :options="managerOptions" @input="setFilter('campaignManager', $event)" />
    </div>

    <div v-if="loadError" class="paa-error">{{ loadError }}</div>

    <div class="paa-stat-row">
      <span class="paa-muted">{{ stats.ordersCount || 0 }} content only orders in range (outside a package, billed per page)</span>
      <span class="paa-spacer"></span>
      <b>Total paid: {{ money(totalPaid) || '$0.00' }}</b>
    </div>

    <div class="paa-card paa-tblwrap">
      <table class="paa-tbl">
        <thead>
          <tr>
            <th :class="thClass('userId')" @click="sortBy('userId')">{{ sortMark('userId') }}User ID</th>
            <th :class="thClass('userEmail')" @click="sortBy('userEmail')">{{ sortMark('userEmail') }}Email</th>
            <th :class="thClass('id', 'paa-r')" @click="sortBy('id')">{{ sortMark('id') }}Content Order ID</th>
            <th :class="thClass('orderDate')" @click="sortBy('orderDate')">{{ sortMark('orderDate') }}Order Date</th>
            <th :class="thClass('amount', 'paa-r')" @click="sortBy('amount')">{{ sortMark('amount') }}Amount ($)</th>
            <th>Delivery date</th>
            <th>Status</th>
            <th>SEO Manager</th>
            <template v-for="pkg in packages">
              <th :key="`${pkg.key}-qty`" class="paa-r paa-wrap">{{ columnLabel(pkg) }}</th>
              <th :key="`${pkg.key}-price`" class="paa-r paa-wrap">$ {{ columnLabel(pkg) }}</th>
            </template>
          </tr>
        </thead>
        <tbody>
          <tr v-for="row in records" :key="row.key">
            <td>{{ row.userId }}</td>
            <td>
              <a :href="userAccountsHref(row.userEmail)" target="_blank" rel="noopener">{{ row.userEmail }}</a>
              <span class="paa-sub">{{ row.siteName }}</span>
            </td>
            <td class="paa-r">{{ row.id }}</td>
            <td>{{ row.orderDate || '' }}</td>
            <td class="paa-r paa-num">{{ money(row.amount) }}</td>
            <td>{{ dateOnly(row.deliveryDate) }}</td>
            <td><pa-admin-badge :badge="row.orderStatus" /></td>
            <td>{{ row.manager ? row.manager.name : '' }}</td>
            <template v-for="pkg in packages">
              <td :key="`${pkg.key}-qty`" class="paa-r">{{ quantityOf(row, pkg.key) }}</td>
              <td :key="`${pkg.key}-price`" class="paa-r">{{ priceOf(row, pkg.key) }}</td>
            </template>
          </tr>
          <tr v-if="!records.length">
            <td class="paa-empty" :colspan="8 + packages.length * 2">{{ loading ? 'Loading…' : 'No content order matches this search.' }}</td>
          </tr>
        </tbody>
      </table>
    </div>

    <pa-admin-pager :page="page" :page-count="pageCount" @change="setPage" />
  </div>
</template>

<script>
import PaAdminBadge from './PaAdminBadge.vue'
import PaAdminFilter from './PaAdminFilter.vue'
import PaAdminPager from './PaAdminPager.vue'
import PaAdminToolbar from './PaAdminToolbar.vue'
import paAdminListMixin from './paAdminListMixin'

const TIER_LABELS = { top_level: 'Top-Level', supporting: 'Supporting' }

/**
 * Content Orders Timeline: content only orders, the pages a POP Authority customer buys outside
 * the package (for instance more supporting articles for one content plan). The per-type columns
 * come from the content catalogue, like the White Glove content timeline.
 */
export default {
  name: 'AdminPopAuthorityContentOrdersTimeline',
  components: { PaAdminBadge, PaAdminFilter, PaAdminPager, PaAdminToolbar },
  mixins: [paAdminListMixin],
  paAdmin: { action: 'getPopAuthorityContentOrdersForAdmin', sort: 'orderDate', tab: 'content-orders' },
  data () {
    return { packages: [] }
  },
  created () {
    this.$store.dispatch('getAvailableSiloContentReportPackages')
      .then((response) => {
        const data = response && response.data
        if (data && data.status === 'SUCCESS') this.packages = data.packages || []
      })
      .catch(() => {
        this.packages = []
      })
  },
  methods: {
    columnLabel (pkg) {
      return `${TIER_LABELS[pkg.type] || pkg.type} Pages: ${pkg.label}`
    },
    lineOf (row, key) {
      return (row.items_on_checkout || []).find((item) => item.report_key === key) || null
    },
    quantityOf (row, key) {
      const line = this.lineOf(row, key)
      return line ? line.quantity : ''
    },
    priceOf (row, key) {
      const line = this.lineOf(row, key)
      return line ? this.money(Number(line.quantity) * Number(line.unit_amount)) : ''
    },
    exportRows () {
      const header = [
        'User ID', 'Email', 'Project', 'Content Order ID', 'Order Date', 'Amount ($)', 'Delivery date', 'Status',
        'SEO Manager',
      ]
      this.packages.forEach((pkg) => {
        header.push(this.columnLabel(pkg), `$ ${this.columnLabel(pkg)}`)
      })
      return this.exportCsv(header, (row) => {
        const cells = [
          row.userId, row.userEmail, row.siteName, row.id, row.orderDate || '', row.amount,
          this.dateOnly(row.deliveryDate), row.orderStatus ? row.orderStatus.label : '',
          row.manager ? row.manager.name : '',
        ]
        this.packages.forEach((pkg) => {
          const line = this.lineOf(row, pkg.key)
          cells.push(line ? line.quantity : '', line ? Number(line.quantity) * Number(line.unit_amount) : '')
        })
        return cells
      })
    },
  },
}
</script>
